import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../axios";
import AdCard from "./AdCard";
import "./MyAds.css";
import Modal from "./Modal";
import KebabMenu from "./KebabMenu";
import { useNavigate } from "react-router-dom";
import BackLink from "./BackLink";
import { API_BASE_URL } from "../config";

const tabs = [
  { key: "all", label: "Όλα", status: "" },
  { key: "published", label: "Δημοσιευμένα", status: "published" },
  { key: "hidden", label: "Μη δημοσιευμένα", status: "hidden" },
  { key: "draft", label: "Προσχέδια", status: "draft" },
  { key: "archived", label: "Ολοκληρωμένα", status: "closed" },
];

const statusBadgeText = {
  published: "Δημοσιευμένο",
  hidden: "Μη δημοσιευμένα",
  draft: "Προσχέδιο",
  closed: "Ολοκληρωμένο",
};

const mobileStatTooltipText = {
  views: "Προβολές αγγελίας",
  impressions: "Προβολές στη λίστα",
  favorites: "Πόσοι πρόσθεσαν την αγγελία στα αγαπημένα",
  responses: "Ανταποκρίσεις / ενδιαφέρον για την αγγελία",
};

const normalizeImage = (url) => {
  if (!url) return "/no-image.png";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${API_BASE_URL}${url}`;
};

export default function MyAds() {
  const [active, setActive] = useState("all");
  const [statsPeriod, setStatsPeriod] = useState("30");
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [unpubTarget, setUnpubTarget] = useState(null);
  const [unpubReason, setUnpubReason] = useState("");
  const [savingUnpub, setSavingUnpub] = useState(false);
  const [activeMobileTooltip, setActiveMobileTooltip] = useState(null);
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);

  const navigate = useNavigate();

  const publishAd = async (ad) => {
    try {
      await axios.post(`/api/ads/${ad.id}/set_status/`, { status: "published" });
      setAds(prev => prev.map(a => a.id === ad.id ? { ...a, status: "published" } : a));
    } catch (e) {
      alert("Σφάλμα στη δημοσίευση.");
    }
  };

  const deleteAd = async (ad) => {
    try {
      await axios.delete(`/api/ads/${ad.id}/`);
      setAds(prev => prev.filter(a => a.id !== ad.id));
    } catch (e) {
      alert("Δεν μπορείτε να διαγράψετε δημοσιευμένη αγγελία. Αποσύρετέ την πρώτα.");
    }
  };

  const editAd = (ad) => {
    navigate(`/ads/${ad.id}/edit`);
  };

  const duplicateAd = (ad) => {
    navigate(`/create?copyFrom=${ad.id}`);
  };

  const queryStatus = useMemo(() => {
    const t = tabs.find(t => t.key === active);
    return t?.status ?? "";
  }, [active]);

    useEffect(() => {
      const closeTooltip = () => setActiveMobileTooltip(null);

      document.addEventListener("click", closeTooltip);
      document.addEventListener("touchstart", closeTooltip);

      return () => {
        document.removeEventListener("click", closeTooltip);
        document.removeEventListener("touchstart", closeTooltip);
      };
    }, []);

    useEffect(() => {
      return () => {
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
        }
      };
    }, []);

    const startStatLongPress = (e, adId, statKey) => {
      e.stopPropagation();

      longPressTriggeredRef.current = false;

      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }

      longPressTimerRef.current = setTimeout(() => {
        longPressTriggeredRef.current = true;
        setActiveMobileTooltip({ adId, statKey });
      }, 500);
    };

    const endStatLongPress = (e) => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }

      if (longPressTriggeredRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const cancelStatLongPress = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };

    const handleStatClick = (e, adId, statKey) => {
      if (longPressTriggeredRef.current) {
        e.preventDefault();
        e.stopPropagation();
        longPressTriggeredRef.current = false;
        return;
      }

      if (activeMobileTooltip?.adId === adId && activeMobileTooltip?.statKey === statKey) {
        e.preventDefault();
        e.stopPropagation();
        setActiveMobileTooltip(null);
      }
    };

  useEffect(() => {
    let abort = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const params = { period: statsPeriod };
        if (queryStatus) params.status = queryStatus;

        const { data } = await axios.get("/api/ads/my/", { params });

        if (!abort) setAds(Array.isArray(data?.results) ? data.results : data);
      } catch (e) {
        if (!abort) setErr("Κάτι πήγε στραβά. Προσπαθήστε ξανά.");
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => { abort = true; };
  }, [queryStatus, statsPeriod]);

  const closeUnpublishDialog = () => {
    setUnpubTarget(null);
    setUnpubReason("");
  };

  const submitUnpublish = async () => {
    if (!unpubTarget || !unpubReason) return;
    try {
      setSavingUnpub(true);
      await axios.post(`/api/ads/${unpubTarget.id}/set_status/`, {
        status: unpubReason,
        reason: unpubReason === "closed" ? "completed" : "changed_plans",
      });

      setAds(prev =>
        prev.map(a => (a.id === unpubTarget.id ? { ...a, status: unpubReason } : a))
      );

      closeUnpublishDialog();
    } catch (e) {
      alert("Κάτι πήγε στραβά. Προσπαθήστε ξανά.");
    } finally {
      setSavingUnpub(false);
    }
  };

  return (
    <div className="myads-container">
      <BackLink label="Πίσω" />
      <h2 className="myads-title">Οι αγγελίες μου</h2>

      <div className="myads-toolbar">
        <div className="myads-tabs">
          {tabs.map(t => (
            <button
              key={t.key}
              type="button"
              className={`myads-tab ${active === t.key ? "is-active" : ""}`}
              onClick={() => setActive(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="myads-periods">
          <button
            type="button"
            className={`myads-period-btn ${statsPeriod === "1" ? "is-active" : ""}`}
            onClick={() => setStatsPeriod("1")}
          >
            1 ημέρα
          </button>

          <button
            type="button"
            className={`myads-period-btn ${statsPeriod === "7" ? "is-active" : ""}`}
            onClick={() => setStatsPeriod("7")}
          >
            7 ημέρες
          </button>

          <button
            type="button"
            className={`myads-period-btn ${statsPeriod === "30" ? "is-active" : ""}`}
            onClick={() => setStatsPeriod("30")}
          >
            30 ημέρες
          </button>

          <button
            type="button"
            className={`myads-period-btn ${statsPeriod === "all" ? "is-active" : ""}`}
            onClick={() => setStatsPeriod("all")}
          >
            Όλο το διάστημα
          </button>
        </div>
      </div>

      {loading && <div className="myads-loader">Φόρτωση...</div>}
      {err && <div className="myads-error">{err}</div>}

      {!loading && !err && (
        ads.length === 0 ? (
          <div className="myads-empty">Δεν υπάρχουν αγγελίες.</div>
        ) : (
          <div className="ads-grid myads-grid">
            {ads.map((ad) => {
              const imageUrl = normalizeImage(ad?.images?.[0]?.image);

              return (
                <div key={ad.id} className="myads-card">
                  <div className="myads-card-desktop">
                    <div className="myads-card-body">
                      {ad.status && (
                        <span
                          className={[
                            "myads-status-badge",
                            `myads-status-${ad.status}`,
                          ].join(" ")}
                          title={ad.status}
                        >
                          {statusBadgeText[ad.status] ?? ad.status}
                        </span>
                      )}

                      <AdCard ad={ad} />

                      <div className="myads-stats">
                        <div className="myads-stat">
                          <span className="myads-stat-value">{ad.stats_impressions ?? 0}</span>
                          <span className="myads-stat-label">Προβολές στη λίστα</span>
                        </div>

                        <div className="myads-stat">
                          <span className="myads-stat-value">{ad.stats_views ?? 0}</span>
                          <span className="myads-stat-label">Προβολές αγγελίας</span>
                        </div>

                        <div className="myads-stat">
                          <span className="myads-stat-value">{ad.stats_favorites ?? 0}</span>
                          <span className="myads-stat-label">Αγαπημένα</span>
                        </div>

                        <div className="myads-stat">
                          <span className="myads-stat-value">{ad.stats_responses ?? 0}</span>
                          <span className="myads-stat-label">Ανταποκρίσεις</span>
                        </div>
                      </div>
                    </div>

                    <div className="myads-actions" style={{ gap: 8, alignItems: "center" }}>
                      {ad.status === "published" ? (
                        <>
                          <button
                            className="green-button"
                            onClick={() => setUnpubTarget(ad)}
                          >
                            Αφαίρεση από τη δημοσίευση
                          </button>

                          <KebabMenu
                            items={[
                              { label: "Επεξεργασία", onClick: () => editAd(ad) },
                              { label: "Δημοσίευση παρόμοιας αγγελίας", onClick: () => duplicateAd(ad) },
                            ]}
                          />
                        </>
                      ) : (
                        <>
                          <button
                            className="green-button"
                            onClick={() => publishAd(ad)}
                          >
                            Δημοσίευση
                          </button>

                          <KebabMenu
                            items={[
                              { label: "Επεξεργασία", onClick: () => editAd(ad) },
                              { label: "Διαγραφή", onClick: () => deleteAd(ad) },
                            ]}
                          />
                        </>
                      )}
                    </div>
                  </div>

                  <div className="myads-mobile-item">
                    <div
                      className="myads-mobile-main"
                      onClick={() => navigate(`/ads/${ad.id}`)}
                    >
                      <div className="myads-mobile-thumb-wrap">
                        <img
                          src={imageUrl}
                          alt={ad.title}
                          className="myads-mobile-thumb"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "/no-image.png";
                          }}
                        />
                      </div>

                      <div className="myads-mobile-content">
                        <div className="myads-mobile-topline">
                          <div className="myads-mobile-title">{ad.title}</div>
                          <div className="myads-mobile-price">{Number(ad.price).toFixed(2)} €</div>
                        </div>

                        <div className="myads-mobile-location">{ad.location}</div>

                        <div className="myads-mobile-status">
                          {statusBadgeText[ad.status] ?? ad.status}
                        </div>

                        <div className="myads-mobile-stats-inline">
                          <span
                            className="myads-mobile-stat-chip"
                            onTouchStart={(e) => startStatLongPress(e, ad.id, "views")}
                            onTouchEnd={endStatLongPress}
                            onTouchCancel={cancelStatLongPress}
                            onTouchMove={cancelStatLongPress}
                            onClick={(e) => handleStatClick(e, ad.id, "views")}
                          >
                            👁 {ad.stats_views ?? 0}
                            {activeMobileTooltip?.adId === ad.id && activeMobileTooltip?.statKey === "views" && (
                              <span className="myads-mobile-stat-tooltip">
                                {mobileStatTooltipText.views}
                              </span>
                            )}
                          </span>

                          <span
                            className="myads-mobile-stat-chip"
                            onTouchStart={(e) => startStatLongPress(e, ad.id, "impressions")}
                            onTouchEnd={endStatLongPress}
                            onTouchCancel={cancelStatLongPress}
                            onTouchMove={cancelStatLongPress}
                            onClick={(e) => handleStatClick(e, ad.id, "impressions")}
                          >
                            📋 {ad.stats_impressions ?? 0}
                            {activeMobileTooltip?.adId === ad.id && activeMobileTooltip?.statKey === "impressions" && (
                              <span className="myads-mobile-stat-tooltip">
                                {mobileStatTooltipText.impressions}
                              </span>
                            )}
                          </span>

                          <span
                            className="myads-mobile-stat-chip"
                            onTouchStart={(e) => startStatLongPress(e, ad.id, "favorites")}
                            onTouchEnd={endStatLongPress}
                            onTouchCancel={cancelStatLongPress}
                            onTouchMove={cancelStatLongPress}
                            onClick={(e) => handleStatClick(e, ad.id, "favorites")}
                          >
                            ❤️ {ad.stats_favorites ?? 0}
                            {activeMobileTooltip?.adId === ad.id && activeMobileTooltip?.statKey === "favorites" && (
                              <span className="myads-mobile-stat-tooltip">
                                {mobileStatTooltipText.favorites}
                              </span>
                            )}
                          </span>

                          <span
                            className="myads-mobile-stat-chip"
                            onTouchStart={(e) => startStatLongPress(e, ad.id, "responses")}
                            onTouchEnd={endStatLongPress}
                            onTouchCancel={cancelStatLongPress}
                            onTouchMove={cancelStatLongPress}
                            onClick={(e) => handleStatClick(e, ad.id, "responses")}
                          >
                            💬 {ad.stats_responses ?? 0}
                            {activeMobileTooltip?.adId === ad.id && activeMobileTooltip?.statKey === "responses" && (
                              <span className="myads-mobile-stat-tooltip">
                                {mobileStatTooltipText.responses}
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="myads-mobile-actions">
                      {ad.status === "published" ? (
                        <>
                          <button
                            className="green-button myads-mobile-primary-btn"
                            onClick={() => setUnpubTarget(ad)}
                          >
                            Αφαίρεση από τη δημοσίευση
                          </button>

                          <KebabMenu
                            items={[
                              { label: "Επεξεργασία", onClick: () => editAd(ad) },
                              { label: "Δημοσίευση παρόμοιας αγγελίας", onClick: () => duplicateAd(ad) },
                            ]}
                          />
                        </>
                      ) : (
                        <>
                          <button
                            className="green-button myads-mobile-primary-btn"
                            onClick={() => publishAd(ad)}
                          >
                            Δημοσίευση
                          </button>

                          <KebabMenu
                            items={[
                              { label: "Επεξεργασία", onClick: () => editAd(ad) },
                              { label: "Διαγραφή", onClick: () => deleteAd(ad) },
                            ]}
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {unpubTarget && (
        <Modal isOpen={!!unpubTarget} onClose={closeUnpublishDialog}>
          <div style={{ textAlign: "left" }}>
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>Αφαίρεση από τη δημοσίευση</h3>

            <p>Καλύψατε την ανάγκη σας στο DEAL;</p>

            <div className="reason-group">
              <label className="reason-option">
                <input
                  type="radio"
                  name="unpub-reason"
                  value="closed"
                  checked={unpubReason === "closed"}
                  onChange={(e) => setUnpubReason(e.target.value)}
                />
                Ναι — μετακίνηση στις «Ολοκληρωμένες»
              </label>

              <label className="reason-option">
                <input
                  type="radio"
                  name="unpub-reason"
                  value="hidden"
                  checked={unpubReason === "hidden"}
                  onChange={(e) => setUnpubReason(e.target.value)}
                />
                Άλλαξαν τα σχέδιά μου — μετακίνηση στις «Μη δημοσιευμένες»
              </label>
            </div>

            <div className="myads-modal-actions">
              <button className="blue-button" onClick={closeUnpublishDialog}>
                Ακύρωση
              </button>
              <button
                className="green-button"
                disabled={!unpubReason || savingUnpub}
                onClick={submitUnpublish}
              >
                {savingUnpub ? "Αποθήκευση..." : "Συνέχεια"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
